import { msg } from "@lingui/core/macro";
import { queryKeys, safeRedirectTarget, sharedApi } from "@tendly/shared";
import { api } from "@/lib/api";
import { registerAccountFromResponse } from "@/lib/auth";
import { writeTabAccountId } from "@/lib/tab-account";
import { queryClient } from "@/lib/query-client";
import { sessionActor } from "@/lib/session-machine";

type Translate = (message: ReturnType<typeof msg>) => string;
type Navigate = (opts: { to: string; replace?: boolean }) => unknown;

/** Human-readable message for each error code the backend can send. */
export function socialErrorMessage(t: Translate, code: string): string {
  switch (code) {
    case "provider_denied": {
      return t(msg`You cancelled the sign-in. No changes were made.`);
    }
    case "provider_unavailable":
    case "exchange_failed": {
      return t(
        msg`The provider is temporarily unavailable. Please try again or use another method.`,
      );
    }
    case "account_conflict": {
      return t(msg`That social account is already linked to a different user.`);
    }
    case "email_required": {
      return t(msg`The provider did not share an email, which is required to sign in.`);
    }
    case "account_banned": {
      return t(msg`This account is not allowed to sign in.`);
    }
    case "invalid_flow":
    case "missing_flow":
    case "state_mismatch": {
      return t(msg`The sign-in session expired. Please try again.`);
    }
    default: {
      return t(msg`Could not complete the sign-in. Please try again.`);
    }
  }
}

function extractUserId(res: unknown): string | null {
  if (typeof res !== "object" || res === null || !("user" in res)) {
    return null;
  }
  const { user } = res;
  if (typeof user !== "object" || user === null || !("id" in user) || typeof user.id !== "string") {
    return null;
  }
  return user.id;
}

export interface FinishSocialLoginOptions {
  navigate: Navigate;
  redirect?: string;
  oauthToken?: string;
  onUserResolved?: (userId: string) => Promise<void>;
}

export async function finishSocialLogin(options: FinishSocialLoginOptions): Promise<void> {
  const { navigate, oauthToken, onUserResolved } = options;
  const redirect = safeRedirectTarget(options.redirect ?? "/");
  let navigated = false;

  try {
    const result = oauthToken
      ? await api.GET("/api/auth/get-session", {
          params: { query: { jwt: true } },
          headers: { Authorization: `Bearer ${oauthToken}` },
        })
      : await sharedApi.getSession(api);
    const data = result.data ?? null;

    const userId = extractUserId(data);
    if (userId && onUserResolved) {
      await onUserResolved(userId);
    }

    const account = await registerAccountFromResponse(data, oauthToken);
    sessionActor.send({ type: "SERVER_REPLY", session: data, error: null });
    queryClient.setQueryData(queryKeys.auth.session(), data);

    if (account) {
      writeTabAccountId(account.userId);
      await queryClient.refetchQueries();
      void navigate({ to: redirect, replace: true });
      navigated = true;
      return;
    }
    await queryClient.refetchQueries();
  } catch {
    // Fail-safe
  } finally {
    if (!navigated) {
      void navigate({ to: redirect, replace: true });
    }
  }
}
